import { Icon, Logo } from "@beresta/ui";
import { useEffect } from "react";
import { t } from "../i18n.js";
import { TABS, type Tab } from "./places.js";

/**
 * Бічна панель ноутбука (картка DesktopShell): «Береста» зі знаком, жовта
 * «Писати» з клавішею N, пошук із ⌘K, чотири розділи з ⌘1–⌘4. Вибраний
 * розділ — як вкладка на телефоні: лайм, рамка, тінь.
 *
 * Від 1440 панель розгорнута на 240 (`sidebar`), 1024–1439 — смуга іконок
 * з підписами на 72 (`sidebar-rail`). Нижче 1024 її немає зовсім: там
 * нижня панель. Що видно — вирішує CSS, а не стан застосунку.
 *
 * Кожна дія з клавішею має й кнопку, а клавіша підписана поруч.
 */
export interface SidebarProps {
  readonly current: Tab;
  readonly inbox: number;
  readonly onTab: (tab: Tab) => void;
  readonly onWrite: () => void;
  readonly onSearch: () => void;
}

const MAC = typeof navigator !== "undefined" && /Mac|iP(hone|ad)/u.test(navigator.platform);
const MOD = MAC ? "⌘" : "Ctrl+";
const PLACES = TABS.filter((p): p is (typeof TABS)[number] & { tab: Tab } => p.tab !== "write");

export function Sidebar({ current, inbox, onTab, onWrite, onSearch }: SidebarProps) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const target = e.target as HTMLElement | null;
      const typing = target?.closest("input, textarea, [contenteditable='true']") !== null;
      const mod = MAC ? e.metaKey : e.ctrlKey;
      if (mod && e.key.toLowerCase() === "k") {
        e.preventDefault();
        onSearch();
        return;
      }
      if (mod && /^[1-4]$/u.test(e.key)) {
        const place = PLACES[Number(e.key) - 1];
        if (place) {
          e.preventDefault();
          onTab(place.tab);
        }
        return;
      }
      if (!typing && !mod && !e.altKey && e.key.toLowerCase() === "n") {
        e.preventDefault();
        onWrite();
      }
    }
    globalThis.addEventListener("keydown", onKey);
    return () => globalThis.removeEventListener("keydown", onKey);
  }, [onTab, onWrite, onSearch]);

  return (
    <aside className="sb" aria-label={t("nav.tabs")}>
      <div className="brand">
        <span className="brand-lg">
          <Logo size={22} />
        </span>
        <span className="headline sb-wide">{t("about.brand")}</span>
      </div>
      <button type="button" className="wbtn headline" title={t("sidebar.writeTip", { key: "N" })} onClick={onWrite}>
        <Icon name="write" />
        <span className="sb-wide">{t("tab.write")}</span>
        <span className="sp sb-wide" />
        <kbd className="sb-wide">{t("sidebar.keyN")}</kbd>
      </button>
      <button type="button" className="sfind subhead" title={`${t("nav.search")} · ${MOD}K`} onClick={onSearch}>
        <Icon name="search" size={18} />
        <span className="sb-wide">{t("nav.search")}</span>
        <span className="sp sb-wide" />
        <kbd className="sb-wide">{`${MOD}K`}</kbd>
      </button>
      <nav className="navl">
        {PLACES.map(({ tab, label, icon }, i) => (
          <button
            key={tab}
            type="button"
            className="ni body-ui"
            aria-current={current === tab ? "page" : undefined}
            title={`${t(label)} · ${MOD}${i + 1}`}
            onClick={() => onTab(tab)}
          >
            <Icon name={icon} />
            <span className="ni-l">{t(label)}</span>
            <span className="sp sb-wide" />
            {tab === "records" && inbox > 0 && <span className="cnt caption-1">{inbox}</span>}
            <kbd className="sb-wide">{`${MOD}${i + 1}`}</kbd>
          </button>
        ))}
      </nav>
    </aside>
  );
}
